/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@reach/router';
import classnames from 'classnames';

/**
 * This is the Menu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */

const Menu = forwardRef(
  ({ id, controller, items, active, onClick, onClose }, ref) => (
    <nav
      ref={ref}
      id={id}
      className="menu"
      role="menu"
      aria-labelledby={controller}
    >
      <div className="items">
        {items.map(({ id, url, text, active: activeLink }) => {
          if (url) {
            return (
              <Link
                key={id}
                to={url}
                className={classnames({ active: activeLink })}
                role="menuitem"
                tabIndex={active ? 0 : -1}
                dangerouslySetInnerHTML={{ __html: text }}
                onClick={() => {
                  onClick(id);
                  onClose();
                }}
              />
            );
          }

          return <span key={id} dangerouslySetInnerHTML={{ __html: text }} />;
        })}
      </div>
    </nav>
  )
);

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Menu.propTypes = {
  id: PropTypes.string,
  controller: PropTypes.string,
  active: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string.isRequired,
      active: PropTypes.bool,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Menu.defaultProps = {
  controller: 'button',
  active: false,
  items: [],
  onClose: () => {},
  onClick: () => {},
};

export default Menu;
